import * as React from 'react';

import {Box, FormControl, MenuItem, Select, Stack, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {AggregationSurveyThemes} from 'crqlar-react-common/dist/models/aggregation/surveyAggregation.model';
import {SurveyJourneyStage} from 'crqlar-react-common/dist/models/survey/surveyFeedbackTheme.model';
import {useTranslation} from 'react-i18next';

import {CardHeadline} from '../../../insights/kit/Headline';
import {NEGATIVE_COLOR, NEUTRAL_COLOR, POSITIVE_COLOR} from "../../../insights/kit/insightsColors";
import {BAR_DRAW_DURATION, BAR_RADIUS, FLOATING_SHADOW} from "../constants";
import {SeriesCard} from '../../../insights/kit/SeriesCard';
import {useInsightsFormatters} from "../../../insights/kit/insightsFormat";
import {InsightsLegend, LegendEntry} from '../../../insights/kit/InsightsLegend';
import {InsightsLoading} from '../../../insights/kit/insightsLoading';
import {InsightsColumn, InsightsTable} from '../../../insights/kit/InsightsTable';
import {InsightsTooltip, TooltipRow, TooltipTitle} from '../../../insights/kit/InsightsTooltip';
import {surveyJourneyStageLabelKey} from '../data/surveyThemeStages.util';

/*
 * A stacked bar of its own rather than the insights' 12px rails: thick enough to carry its counts, the
 * segments touching, and only the bar's two outer ends rounded.
 */
const BAR_THICKNESS = 22;
const COMPARE_THICKNESS = 8;
const SEGMENT_GAP = 1;

/** The narrowest segment a count is printed in. */
const COUNT_MIN_WIDTH = 24;

const LABEL_WIDTH = {xs: 112, sm: 140};

type SentimentKey = 'positive' | 'neutral' | 'negative';

/** Left to right as the bars run: complaints left of the centre line, mentions in passing across it, praise right. */
const DIVERGING_ORDER: SentimentKey[] = ['negative', 'neutral', 'positive'];

const SENTIMENT_COLORS: Record<SentimentKey, string> = {
    positive: POSITIVE_COLOR,
    neutral: NEUTRAL_COLOR,
    negative: NEGATIVE_COLOR,
};

const COMPARE_ALPHA = 0.35;

type SortKey = 'worst' | 'best' | 'complaints' | 'mentions';

const SORT_KEYS: SortKey[] = ['worst', 'best', 'complaints', 'mentions'];

/** Where a segment sits on the bar's track, in percent of its width; the centre line is at 50. */
interface Span {
    left: number;
    width: number;
}

interface ThemeRow {
    id: string;
    name: string;
    stage: SurveyJourneyStage;
    archived: boolean;

    mentions: number;
    counts: Record<SentimentKey, number>;
    shares: Record<SentimentKey, number>;
    spans: Record<SentimentKey, Span>;

    compareMentions: number | null;
    compareSpans: Record<SentimentKey, Span> | null;
    delta: number | null;
}

/** Praise minus complaints, in mentions: one complaint does not outrank nine more complaints than praise. */
const balanceOf = (row: ThemeRow) => row.counts.positive - row.counts.negative;

const SORTERS: Record<SortKey, (left: ThemeRow, right: ThemeRow) => number> = {
    worst: (left, right) =>
        balanceOf(left) - balanceOf(right) || right.counts.negative - left.counts.negative || left.name.localeCompare(right.name),
    best: (left, right) =>
        balanceOf(right) - balanceOf(left) || right.counts.positive - left.counts.positive || left.name.localeCompare(right.name),
    complaints: (left, right) =>
        right.counts.negative - left.counts.negative || right.mentions - left.mentions || left.name.localeCompare(right.name),
    mentions: (left, right) =>
        right.mentions - left.mentions || right.counts.negative - left.counts.negative || left.name.localeCompare(right.name),
};

interface ThemeLabels {
    theme: string;
    category: string;
    mentions: string;
    compare: string;
    change: string;
    archived: string;
    sentiment: Record<SentimentKey, string>;
}

interface ThemesCardProps {
    series?: AggregationSurveyThemes;
    loading?: InsightsLoading;
}

/**
 * Neutral is split across the centre line, so a theme's complaints and its praise both start from the same
 * place and only their lengths differ. `unit` is the share of the track one mention takes, the same for
 * every row and both periods, so a longer bar always means more mentions.
 */
function spansOf(counts: Record<SentimentKey, number>, unit: number): Record<SentimentKey, Span> {
    const halfNeutral = (counts.neutral / 2) * unit;

    return {
        negative: {left: 50 - halfNeutral - counts.negative * unit, width: counts.negative * unit},
        neutral: {left: 50 - halfNeutral, width: counts.neutral * unit},
        positive: {left: 50 + halfNeutral, width: counts.positive * unit},
    };
}

const reachOf = (counts: Record<SentimentKey, number>) =>
    Math.max(counts.negative + counts.neutral / 2, counts.positive + counts.neutral / 2);

/** An element's width in pixels, kept current as the card resizes. */
function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
    const ref = React.useRef<T>(null);
    const [width, setWidth] = React.useState(0);

    React.useEffect(() => {
        const element = ref.current;
        if (element == null || typeof ResizeObserver === 'undefined') return;

        const observer = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    return [ref, width];
}

interface DivergingBarProps {
    spans: Record<SentimentKey, Span>;
    height: number;
    muted?: boolean;
    /** Printed inside the segments that are wide enough; the compare bar carries none. */
    counts?: Record<SentimentKey, number>;
    formatCount?: (value: number) => string;
}

/**
 * Shown at full size on load - no draw - and slid to the new spans when the period changes. The gap between the
 * segments is inset, so the spans stay exact.
 */
function DivergingBar({spans, height, muted, counts, formatCount}: DivergingBarProps) {
    const [ref, width] = useWidth<HTMLDivElement>();

    const drawn = DIVERGING_ORDER.filter(key => spans[key].width > 0);
    const outerLeft = drawn[0];
    const outerRight = drawn[drawn.length - 1];

    return (
        <Box ref={ref} sx={{position: 'relative', height}}>
            {drawn.map(key => {
                const left = key === outerLeft ? BAR_RADIUS : 0;
                const right = key === outerRight ? BAR_RADIUS : 0;
                const fits = counts != null && formatCount != null && (spans[key].width / 100) * width >= COUNT_MIN_WIDTH;

                return (
                    <Box
                        key={key}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: `${spans[key].left}%`,
                            width: `${spans[key].width}%`,
                            transition: `left ${BAR_DRAW_DURATION}ms ease-out, width ${BAR_DRAW_DURATION}ms ease-out`,
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                left: SEGMENT_GAP / 2,
                                right: SEGMENT_GAP / 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                borderRadius: `${left}px ${right}px ${right}px ${left}px`,
                                bgcolor: muted ? alpha(SENTIMENT_COLORS[key], COMPARE_ALPHA) : SENTIMENT_COLORS[key],
                            }}
                        >
                            {fits && (
                                <Box
                                    component={'span'}
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        lineHeight: 1,
                                        whiteSpace: 'nowrap',
                                        fontVariantNumeric: 'tabular-nums',
                                        color: key === 'neutral' ? 'text.primary' : 'common.white',
                                    }}
                                >
                                    {formatCount(counts[key])}
                                </Box>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

interface ThemeBarProps {
    row: ThemeRow;
    category: string;
    labels: ThemeLabels;
    hasCompare: boolean;
    formatCount: (value: number) => string;
    formatPercent: (value: number) => string;
}

function ThemeBar({row, category, labels, hasCompare, formatCount, formatPercent}: ThemeBarProps) {
    const tooltip = (
        <React.Fragment>
            <TooltipTitle title={row.name} subtitle={category}/>

            {DIVERGING_ORDER.map(key => (
                <TooltipRow
                    key={key}
                    swatch={SENTIMENT_COLORS[key]}
                    label={labels.sentiment[key]}
                    value={`${formatCount(row.counts[key])} · ${formatPercent(row.shares[key])}`}
                />
            ))}

            <TooltipRow label={labels.mentions} value={formatCount(row.mentions)}/>

            {row.compareMentions != null && (
                <TooltipRow
                    swatch={alpha(POSITIVE_COLOR, COMPARE_ALPHA)}
                    label={labels.compare}
                    value={formatCount(row.compareMentions)}
                />
            )}
        </React.Fragment>
    );

    const stageText = row.archived ? `${category} · ${labels.archived}` : category;

    return (
        <InsightsTooltip followCursor placement={'top'} title={tooltip}>
            <Stack
                direction={'row'}
                alignItems={'stretch'}
                gap={1.5}
                sx={theme => ({
                    minWidth: 0,
                    cursor: 'default',
                    px: 1,
                    mx: -1,
                    borderRadius: 1.5,
                    transition: 'background-color 120ms ease',
                    '&:hover': {backgroundColor: alpha(theme.palette.text.primary, 0.05)},
                })}
            >
                {/* A long name ends in an ellipsis; the full one is in the tooltip. */}
                <Stack justifyContent={'center'} sx={{width: LABEL_WIDTH, flexShrink: 0, minWidth: 0, py: 0.75}}>
                    <Typography noWrap title={row.name} sx={{color: 'text.primary', fontSize: 14, fontWeight: 600}}>
                        {row.name}
                    </Typography>

                    <Typography noWrap title={stageText} sx={{color: 'text.secondary', fontSize: 12}}>
                        {stageText}
                    </Typography>
                </Stack>

                <Stack justifyContent={'center'} gap={0.5} sx={{position: 'relative', flexGrow: 1, minWidth: 0}}>
                    {/* Behind the bars, so it never runs through a count; it shows between the rows. */}
                    <Box
                        sx={theme => ({
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: '50%',
                            width: '1px',
                            ml: '-0.5px',
                            bgcolor: alpha(theme.palette.text.primary, 0.3),
                        })}
                    />

                    <DivergingBar
                        spans={row.spans}
                        counts={row.counts}
                        formatCount={formatCount}
                        height={BAR_THICKNESS}
                    />

                    {hasCompare && row.compareSpans != null && (
                        <DivergingBar muted spans={row.compareSpans} height={COMPARE_THICKNESS}/>
                    )}
                </Stack>

                {/* Mirrors the label column, so the centre line sits in the middle of the card. Not on a phone. */}
                <Box sx={{width: LABEL_WIDTH, flexShrink: 0, display: {xs: 'none', sm: 'block'}}}/>
            </Stack>
        </InsightsTooltip>
    );
}

interface ThemeTableProps {
    rows: ThemeRow[];
    labels: ThemeLabels;
    categoryOf: (stage: SurveyJourneyStage) => string;
    hasCompare: boolean;
    formatCount: (value: number) => string;
    formatPercent: (value: number) => string;
    formatPoints: (value: number) => string;
}

function ThemeTable({rows, labels, categoryOf, hasCompare, formatCount, formatPercent, formatPoints}: ThemeTableProps) {
    const columns: Array<InsightsColumn<ThemeRow> | false> = [
        {key: 'theme', kind: 'label', label: labels.theme, cell: row => row.name},
        {key: 'category', kind: 'text', label: labels.category, cell: row => categoryOf(row.stage)},
        {key: 'mentions', kind: 'value', label: labels.mentions, cell: row => row.mentions, format: formatCount},

        hasCompare && {key: 'compare', kind: 'compare', label: labels.compare, cell: row => row.compareMentions, format: formatCount},
        hasCompare && {key: 'change', kind: 'delta', label: labels.change, cell: row => row.delta, format: formatPoints, comparison: true},

        {key: 'positive', kind: 'detail', label: labels.sentiment.positive, cell: row => row.shares.positive, format: formatPercent},

        {key: 'negative', kind: 'detail', label: labels.sentiment.negative, cell: row => row.shares.negative, format: formatPercent},
    ];

    return <InsightsTable label={labels.theme} columns={columns} rows={rows} rowKey={row => row.id}/>;
}

export function ThemesCard({series, loading}: ThemesCardProps) {
    const {t} = useTranslation();

    const [sort, setSort] = React.useState<SortKey>('complaints');
    const hasCompare = series?.compareRange != null;
    const {count: formatCount, percent: formatPercent, points: formatPoints, decimal: formatDecimal} = useInsightsFormatters();

    const labels = React.useMemo<ThemeLabels>(
        () => ({
            theme: t('Theme'),
            category: t('Category'),
            mentions: t('Mentions'),
            compare: t('Compare period'),
            change: t('Change'),
            archived: t('Archived'),
            sentiment: {
                positive: t('Positive'),
                neutral: t('Neutral'),
                negative: t('Negative'),
            },
        }),
        [t],
    );

    const sortLabels = React.useMemo<Record<SortKey, string>>(
        () => ({
            worst: t('Worst first'),
            best: t('Best first'),
            complaints: t('Most complaints'),
            mentions: t('Most mentions'),
        }),
        [t],
    );

    const categoryOf = React.useCallback((stage: SurveyJourneyStage) => t(surveyJourneyStageLabelKey(stage)), [t]);

    const rows = React.useMemo<ThemeRow[]>(() => {
        const mentioned = (series?.themes ?? []).filter(theme => theme.current.mentions > 0 || (theme.compare?.mentions ?? 0) > 0);

        const countsOf = (counts: {positive: number; neutral: number; negative: number}): Record<SentimentKey, number> => ({
            positive: counts.positive,
            neutral: counts.neutral,
            negative: counts.negative,
        });

        // One scale for every row and both periods: the longest half of any bar reaches the card's edge.
        const longest = mentioned.reduce(
            (max, theme) => Math.max(max, reachOf(countsOf(theme.current)), theme.compare == null ? 0 : reachOf(countsOf(theme.compare))),
            0,
        );
        const unit = longest === 0 ? 0 : 50 / longest;

        const share = (value: number, base: number) => (base === 0 ? 0 : (value / base) * 100);

        return mentioned
            .map<ThemeRow>(theme => {
                const counts = countsOf(theme.current);
                const compare = theme.compare;

                return {
                    id: theme.themeId,
                    name: theme.name,
                    stage: theme.stage,
                    archived: theme.archived,
                    mentions: theme.current.mentions,
                    counts,
                    shares: {
                        positive: share(counts.positive, theme.current.mentions),
                        neutral: share(counts.neutral, theme.current.mentions),
                        negative: share(counts.negative, theme.current.mentions),
                    },
                    spans: spansOf(counts, unit),
                    compareMentions: compare?.mentions ?? null,
                    compareSpans: compare == null ? null : spansOf(countsOf(compare), unit),
                    delta: compare == null ? null : theme.current.mentions - compare.mentions,
                };
            })
            .sort(SORTERS[sort]);
    }, [series, sort]);

    const mentions = series?.summary.mentions ?? 0;
    const previousMentions = series?.summary.previousMentions ?? null;
    const days = series?.range.days ?? null;
    const compareDays = series?.compareRange?.days ?? null;
    const sameLength = days != null && compareDays != null && days === compareDays;

    const perDay = (value: number, over: number) => value / over;

    const delta =
        previousMentions == null || days == null || compareDays == null
            ? null
            : sameLength
                ? mentions - previousMentions
                : perDay(mentions, days) - perDay(previousMentions, compareDays);

    const formatChange = sameLength ? formatCount : formatDecimal;

    const headline = (
        <CardHeadline
            value={formatCount(mentions)}
            suffix={t('mentions')}
            change={delta == null || previousMentions == null || days == null || compareDays == null ? null : {
                title: labels.mentions,
                subtitle: sameLength ? undefined : t('Per day, as the two ranges differ in length'),
                current: sameLength ? formatCount(mentions) : formatChange(perDay(mentions, days)),
                previous: sameLength ? formatCount(previousMentions) : formatChange(perDay(previousMentions, compareDays)),
                delta,
                formatDelta: formatChange,
                compareLabel: labels.compare,
            }}
        />
    );

    const legend = (
        <InsightsLegend>
            {DIVERGING_ORDER.map(key => (
                <LegendEntry key={key} shape={'bar'} color={SENTIMENT_COLORS[key]} label={labels.sentiment[key]}/>
            ))}

            {hasCompare && <LegendEntry shape={'bar'} color={alpha(POSITIVE_COLOR, COMPARE_ALPHA)} label={labels.compare}/>}
        </InsightsLegend>
    );

    // In the header from sm up. A phone's header has no room left beside the title, so there it sits above the bars.
    const sortPicker = (placement: 'header' | 'toolbar') => rows.length > 1 && (
        <FormControl
            size={'small'}
            fullWidth={placement === 'toolbar'}
            sx={placement === 'header'
                ? {display: {xs: 'none', sm: 'inline-flex'}, minWidth: 180, maxWidth: 280}
                : {display: {xs: 'flex', sm: 'none'}, mb: 2}}
        >
            <Select<SortKey>
                value={sort}
                onChange={event => setSort(event.target.value as SortKey)}
                renderValue={value => `${t('Sort by')}: ${sortLabels[value]}`}
                inputProps={{'aria-label': t('Sort by')}}
                sx={{height: 36, '& .MuiSelect-select': {py: 0.75}}}
                MenuProps={{PaperProps: {variant: 'outlined', sx: {boxShadow: FLOATING_SHADOW}}}}
            >
                {SORT_KEYS.map(key => (
                    <MenuItem key={key} value={key}>
                        {sortLabels[key]}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const empty = rows.length > 0 ? undefined : (
        <Typography variant={'body2'} color={'text.secondary'}>
            {t('No theme was mentioned in this period.')}
        </Typography>
    );

    return (
        <SeriesCard
            title={t('Themes', {context: 'survey'})}
            hint={t('What guests wrote about, grouped into themes. Red left of the centre line is complaints, green right of it is praise, and grey across it was mentioned in passing. The longer the bar, the more often the theme came up.')}
            headline={headline}
            actions={sortPicker('header')}
            toolbar={sortPicker('toolbar')}
            loading={loading}
            legend={legend}
            empty={empty}
            chart={(
                <Stack>
                    {rows.map(row => (
                        <ThemeBar
                            key={row.id}
                            row={row}
                            category={categoryOf(row.stage)}
                            labels={labels}
                            hasCompare={hasCompare}
                            formatCount={formatCount}
                            formatPercent={formatPercent}
                        />
                    ))}
                </Stack>
            )}
            table={(
                <ThemeTable
                    rows={rows}
                    labels={labels}
                    categoryOf={categoryOf}
                    hasCompare={hasCompare}
                    formatCount={formatCount}
                    formatPercent={formatPercent}
                    formatPoints={formatPoints}
                />
            )}
        />
    );
}
